import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader, CardContent } from '@/components/ui/Card';
import { FormInput, FormSelect, FormTextarea } from '@/components/forms/FormField';
import {
  useInvoiceDetail,
  useCreateInvoice,
  useUpdateInvoice,
  type CreateInvoicePayload,
} from './hooks/useBilling';
import { useToast } from '@/components/ui/Toast';
import { useContactsList } from '@/features/contacts/hooks/useContacts';
import { useBillingEntityConfigs } from './hooks/useBillingEntityConfig';
import { useTaxRates } from '@/features/settings/hooks/useTaxRates';
import { JobSearchModal, type SelectedJobWithItems } from './JobSearchModal';
import { useSystemLookups } from '@/features/settings/hooks/useSystemLookups';
import { usePermissions, PERMISSIONS } from '@/hooks/usePermissions';

// ─── Constants ────────────────────────────────────────────────────────────────

const MAX_LINE_ITEMS = 100;

// ─── Validation Schema ────────────────────────────────────────────────────────

const lineItemSchema = z.object({
  description: z.string().min(1, 'Description is required').max(500),
  quantity: z.coerce.number().min(0.01, 'Quantity must be greater than 0').max(99999),
  unitPrice: z.coerce.number().min(0, 'Charges must be 0 or greater').max(9999999.99),
  // taxRate accepts the 3 standard codes OR a UUID from the tax_rates table
  taxRate: z.string().min(1, 'Tax rate is required'),
});

/** Build dynamic schema based on whether due date is required */
function buildInvoiceSchema(requireDueDate: boolean) {
  return z.object({
    clientId: z.string().min(1, 'Client is required'),
    practiceEntityCode: z.string().min(1, 'Practice entity is required'),
    issueDate: z.string().min(1, 'Issue date is required'),
    dueDate: requireDueDate
      ? z.string().min(1, 'Due date is required for this practice entity')
      : z.string().optional(),
    notes: z.string().max(2000).optional(),
    lineItems: z
      .array(lineItemSchema)
      .min(1, 'At least one line item is required')
      .max(MAX_LINE_ITEMS, `Maximum ${MAX_LINE_ITEMS} line items allowed`),
  });
}

type InvoiceFormValues = z.infer<ReturnType<typeof buildInvoiceSchema>>;

/**
 * InvoiceForm — create or edit an invoice with dynamic line items and real-time totals.
 * Requirements: 10.1, 10.2
 */
export function InvoiceForm() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const isEditing = !!id;

  const { data: existingInvoice, isLoading: invoiceLoading } = useInvoiceDetail(id ?? '');
  const createMutation = useCreateInvoice();
  const updateMutation = useUpdateInvoice(id ?? '');

  // Fetch clients for the client dropdown — only contacts with client nature
  const { data: clientsData } = useContactsList({
    contactTypeNature: 'client',
    pageSize: 200,
  });
  const clientOptions = (clientsData?.contacts ?? []).map((c) => ({
    value: c.id,
    label:
      `${c.firstName ?? ''} ${c.lastName ?? ''}`.trim() ||
      ((c as unknown as Record<string, unknown>).businessName as string) ||
      c.email ||
      c.id,
  }));

  // Fetch billing entity configs and tax rates
  const { data: entityConfigs } = useBillingEntityConfigs();
  const { data: taxRates } = useTaxRates();
  const [selectedEntityCode, setSelectedEntityCode] = useState('');

  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { isSubmitting },
  } = useForm<InvoiceFormValues>({
    resolver: zodResolver(buildInvoiceSchema(false)),
    defaultValues: {
      clientId: '',
      practiceEntityCode: '',
      issueDate: new Date().toISOString().split('T')[0],
      dueDate: '',
      notes: '',
      lineItems: [{ description: '', quantity: 1, unitPrice: 0, taxRate: 'hst_13' }],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'lineItems',
  });

  // Job search modal state
  const [showJobSearch, setShowJobSearch] = useState(false);
  const [linkedJob, setLinkedJob] = useState<{
    id: string;
    title: string;
    jobCode: string | null;
  } | null>(null);

  // Discount application state
  const { can, isAdmin } = usePermissions();
  const { data: discountDefinitions } = useSystemLookups('discount_definitions', true);
  const [selectedDiscountId, setSelectedDiscountId] = useState<string>('');

  // Track whether form has been initially populated (to skip entity defaults on edit load)
  // Populate form when editing
  useEffect(() => {
    if (existingInvoice && isEditing) {
      reset({
        clientId: existingInvoice.clientId,
        practiceEntityCode: existingInvoice.billingEntityId ?? '',
        issueDate: existingInvoice.issueDate ? existingInvoice.issueDate.split('T')[0] : '',
        dueDate: existingInvoice.dueDate ? existingInvoice.dueDate.split('T')[0] : '',
        notes: existingInvoice.notes ?? '',
        lineItems: (existingInvoice.lineItems || []).map((item) => ({
          description: item.description,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          taxRate: item.taxRateId || item.taxRate || '',
        })),
      });
    }
  }, [existingInvoice, isEditing, reset]);

  // Watch line items for real-time calculation
  const watchedLineItems = watch('lineItems');
  const watchedEntityCode = watch('practiceEntityCode');

  // Build practice entity dropdown from billing configs
  const entityOptions = (entityConfigs || []).map((c) => ({
    value: c.practiceEntityCode || c.practiceEntityId,
    label: c.billingEntityName || c.practiceEntityCode || c.practiceEntityId,
  }));

  // Default tax rate: first active rate, or empty string if none configured yet
  const defaultTaxRateValue =
    (taxRates || []).find((r) => r.isActive && r.isDefault)?.id ||
    (taxRates || []).find((r) => r.isActive)?.id ||
    '';

  // Resolve the selected entity's default tax rate ID
  const selectedEntityConfig = (entityConfigs || []).find(
    (c) =>
      (c.practiceEntityCode || c.practiceEntityId) === (watchedEntityCode || selectedEntityCode),
  );
  const defaultTaxRateId = selectedEntityConfig?.defaultTaxRateId || null;
  const requireDueDate = selectedEntityConfig?.requireDueDate ?? false;

  // Build tax rate options entirely from the tax_rates table (system lookups)
  // These are configured in Settings → Tax Rates
  const taxRateOptions = (taxRates || [])
    .filter((r) => r.isActive)
    .map((r) => ({
      value: r.id,
      label: `${r.name} (${(r.rate < 1 ? r.rate * 100 : r.rate).toFixed(2)}%)${r.province ? ` — ${r.province}` : ''}`,
    }));

  // Build a lookup map: tax_rate_id → percentage (decimal) for totals calculation
  const taxRatePercentMap = new Map<string, number>(
    (taxRates || []).map((r) => [r.id, r.rate < 1 ? r.rate * 100 : r.rate]),
  );

  // Build a lookup map: tax_rate_id → label for totals display
  const taxRateLabelMap = new Map<string, string>(
    (taxRates || []).map((r) => [
      r.id,
      `${r.name} (${(r.rate < 1 ? r.rate * 100 : r.rate).toFixed(2)}%)`,
    ]),
  );

  // When practice entity changes, apply its defaults to the form
  // On edit: only apply default tax rates when the user manually switches to a DIFFERENT entity,
  // not on initial form population (which preserves user-customized tax rates).
  const previousEntityRef = useRef<string | null>(null);

  useEffect(() => {
    if (!watchedEntityCode) return;

    setSelectedEntityCode(watchedEntityCode);

    // Determine if this is a deliberate user change vs initial population
    const isInitialPopulation = previousEntityRef.current === null;
    const isSameEntity = previousEntityRef.current === watchedEntityCode;

    // Update ref to track current value for next comparison
    previousEntityRef.current = watchedEntityCode;

    // Skip applying defaults if:
    // - This is the first time the entity is being set (initial form load/population)
    // - The entity hasn't actually changed (same value re-emitted)
    if (isInitialPopulation || isSameEntity) {
      return;
    }

    const entityConfig = (entityConfigs || []).find(
      (c) => (c.practiceEntityCode || c.practiceEntityId) === watchedEntityCode,
    );

    if (entityConfig) {
      const currentValues = watch();

      // Compute due date from issue date + entity payment terms
      const paymentTermsDays = entityConfig.defaultPaymentTermsDays || 30;
      const issueDate = currentValues.issueDate || new Date().toISOString().split('T')[0];
      const dueDate = new Date(issueDate);
      dueDate.setDate(dueDate.getDate() + paymentTermsDays);
      const dueDateStr = dueDate.toISOString().split('T')[0];

      // Apply entity default tax rate UUID to all line items
      const updatedLineItems = (currentValues.lineItems || []).map((li) => ({
        ...li,
        taxRate: entityConfig.defaultTaxRateId || li.taxRate,
      }));

      reset({
        ...currentValues,
        dueDate: dueDateStr,
        lineItems: updatedLineItems,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedEntityCode]);

  // ─── Real-time Totals Calculation ───────────────────────────────────────────
  const selectedDiscount = useMemo(() => {
    if (!selectedDiscountId || !discountDefinitions) return null;
    return discountDefinitions.find((d) => d.id === selectedDiscountId) ?? null;
  }, [selectedDiscountId, discountDefinitions]);

  const totals = useMemo(() => {
    let subtotal = 0;
    let totalDiscount = 0;
    const taxByRate: Record<string, { label: string; amount: number }> = {};

    (watchedLineItems ?? []).forEach((item) => {
      const qty = Number(item.quantity) || 0;
      const price = Number(item.unitPrice) || 0;
      const lineAmount = qty * price;
      subtotal += lineAmount;

      // Calculate discount for this line item
      let lineDiscount = 0;
      if (selectedDiscount) {
        const meta = selectedDiscount.metadata as {
          discount_type?: string;
          discount_value?: number;
          service_rules?: Array<{
            service_type: string;
            discount_type: string;
            discount_value: number;
          }>;
        };

        // Check for service-specific rule first (rule-based mode)
        const rules = meta.service_rules ?? [];
        // In rule-based mode, match by service_type if we have one from the linked job
        // For now, apply the default discount to all line items
        if (rules.length > 0) {
          // Rule-based: try to match. If no specific match, no discount for that line.
          // For simplicity, apply first rule's discount (full rule matching requires service_type on line items)
          const firstRule = rules[0];
          if (firstRule) {
            if (firstRule.discount_type === 'percentage') {
              lineDiscount = lineAmount * (firstRule.discount_value / 100);
            } else {
              lineDiscount = Math.min(firstRule.discount_value, lineAmount);
            }
          }
        } else {
          // Standard mode
          if (meta.discount_type === 'percentage') {
            lineDiscount = lineAmount * ((meta.discount_value ?? 0) / 100);
          } else {
            lineDiscount = Math.min(meta.discount_value ?? 0, lineAmount);
          }
        }
      }

      totalDiscount += lineDiscount;
      const afterDiscount = lineAmount - lineDiscount;

      const rateId = item.taxRate as string;
      const rawPct = taxRatePercentMap.get(rateId) ?? 0;
      const taxPct = rawPct > 0 && rawPct < 2 ? rawPct : rawPct / 100;
      const taxAmount = afterDiscount * taxPct;

      if (taxPct > 0) {
        if (!taxByRate[rateId]) {
          const label = taxRateLabelMap.get(rateId) ?? rateId;
          taxByRate[rateId] = { label, amount: 0 };
        }
        taxByRate[rateId].amount += taxAmount;
      }
    });

    const taxTotal = Object.values(taxByRate).reduce((sum, t) => sum + t.amount, 0);
    const grandTotal = subtotal - totalDiscount + taxTotal;

    return { subtotal, totalDiscount, taxByRate, taxTotal, grandTotal };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedLineItems, taxRatePercentMap, taxRateLabelMap, selectedDiscount]);

  // ─── Job Selection Handler ───────────────────────────────────────────────────
  const handleJobSelected = useCallback(
    (result: SelectedJobWithItems) => {
      setShowJobSearch(false);
      setLinkedJob({ id: result.job.id, title: result.job.title, jobCode: result.job.jobCode });

      // Auto-set client if not already set
      const currentClient = watch('clientId');
      if (!currentClient && result.job.clientId) {
        reset({ ...watch(), clientId: result.job.clientId });
      }

      // Populate line items from billable items
      if (result.items.length > 0) {
        const taxRate = defaultTaxRateId || defaultTaxRateValue;
        const newLineItems = result.items.map((item) => ({
          description: item.description,
          quantity: item.hours > 0 ? item.hours : 1,
          unitPrice: item.amount,
          taxRate,
        }));

        // Replace existing empty line items or append
        const current = watch('lineItems');
        const hasOnlyEmpty =
          current.length === 1 && !current[0].description && current[0].unitPrice === 0;
        if (hasOnlyEmpty) {
          reset({ ...watch(), lineItems: newLineItems });
        } else {
          for (const li of newLineItems) {
            append(li);
          }
        }
      }
    },
    [append, defaultTaxRateId, defaultTaxRateValue, reset, watch],
  );

  const onSubmit = async (values: InvoiceFormValues) => {
    // Validate due date if entity requires it
    if (requireDueDate && !values.dueDate) {
      toast({ variant: 'error', title: 'Due date is required for this practice entity' });
      return;
    }

    // Find the actual practice entity UUID for the selected code
    const entityConfig = (entityConfigs || []).find(
      (c) => (c.practiceEntityCode || c.practiceEntityId) === values.practiceEntityCode,
    );

    const payload: CreateInvoicePayload = {
      clientId: values.clientId,
      issueDate: values.issueDate,
      dueDate: values.dueDate || undefined,
      notes: values.notes || undefined,
      jobId: entityConfig?.practiceEntityId ? undefined : undefined, // future: job selector
      lineItems: values.lineItems.map((item) => {
        const lineItem: CreateInvoicePayload['lineItems'][number] = {
          description: item.description,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          taxRate: 'exempt' as const, // legacy field — actual rate applied via taxRateId
          taxRateId: item.taxRate || entityConfig?.defaultTaxRateId || undefined,
        };

        // Apply discount from selected discount definition
        if (selectedDiscount) {
          const meta = selectedDiscount.metadata as {
            discount_type?: string;
            discount_value?: number;
            service_rules?: Array<{
              service_type: string;
              discount_type: string;
              discount_value: number;
            }>;
          };
          const rules = meta.service_rules ?? [];
          if (rules.length > 0 && rules[0]) {
            lineItem.discountType = rules[0].discount_type === 'percentage' ? 'percentage' : 'flat';
            lineItem.discountValue = rules[0].discount_value;
          } else if (meta.discount_value && meta.discount_value > 0) {
            lineItem.discountType = meta.discount_type === 'percentage' ? 'percentage' : 'flat';
            lineItem.discountValue = meta.discount_value;
          }
        }

        return lineItem;
      }),
    };

    try {
      if (isEditing) {
        await updateMutation.mutateAsync({
          clientId: payload.clientId,
          billingEntityId: entityConfig?.practiceEntityId || undefined,
          issueDate: payload.issueDate,
          dueDate: payload.dueDate,
          notes: payload.notes,
          lineItems: payload.lineItems,
        });
        toast({ variant: 'success', title: 'Invoice updated successfully' });
      } else {
        await createMutation.mutateAsync(payload);
        toast({ variant: 'success', title: 'Invoice created successfully' });
      }
      navigate('/billing/invoices');
    } catch {
      toast({
        variant: 'error',
        title: isEditing ? 'Failed to update invoice' : 'Failed to create invoice',
      });
    }
  };

  if (isEditing && invoiceLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex items-center gap-2 text-gray-500" aria-live="polite">
          <svg
            className="h-5 w-5 animate-spin text-brand-500"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
            />
          </svg>
          Loading invoice...
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {isEditing ? 'Edit Invoice' : 'New Invoice'}
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {isEditing
              ? 'Update the invoice details below.'
              : 'Fill in the details to create a new invoice.'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button type="button" variant="outline" onClick={() => navigate('/billing/invoices')}>
            Cancel
          </Button>
          <Button form="invoice-form" type="submit" loading={isSubmitting}>
            {isEditing ? 'Update Invoice' : 'Create Invoice'}
          </Button>
        </div>
      </div>

      <form id="invoice-form" onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-6">
        {/* Entity Default Tax Info */}
        {selectedEntityConfig?.defaultTaxRateId && (
          <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800 dark:border-blue-800 dark:bg-blue-900/20 dark:text-blue-300">
            Default tax rate from{' '}
            <strong>{selectedEntityConfig.billingEntityName || 'selected entity'}</strong>:{' '}
            {(taxRates || []).find((r) => r.id === selectedEntityConfig.defaultTaxRateId)?.name ||
              'configured tax'}{' '}
            will be applied to all line items on this invoice.
          </div>
        )}

        {/* Invoice Details */}
        <Card>
          <CardHeader title="Invoice Details" />
          <CardContent>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              {entityOptions.length > 0 && (
                <FormSelect
                  name="practiceEntityCode"
                  control={control}
                  label="Practice Entity"
                  required
                  options={entityOptions}
                  placeholder="Select practice entity"
                />
              )}
              <FormSelect
                name="clientId"
                control={control}
                label="Client"
                required
                options={clientOptions}
                placeholder="Select a client"
              />
              <FormInput
                name="issueDate"
                control={control}
                label="Issue Date"
                type="date"
                required
              />
              <FormInput
                name="dueDate"
                control={control}
                label="Due Date"
                type="date"
                required={requireDueDate}
                hint={
                  requireDueDate
                    ? 'Required for this practice entity'
                    : selectedEntityConfig
                      ? `Auto-set from ${selectedEntityConfig.defaultPaymentTermsDays ?? 30}-day payment terms`
                      : 'Set automatically from billing configuration'
                }
              />
            </div>
          </CardContent>
        </Card>

        {/* Line Items */}
        <Card>
          <CardHeader title="Line Items" />
          <CardContent>
            <div className="space-y-3">
              {/* Header row */}
              <div className="hidden grid-cols-12 gap-2 text-xs font-medium uppercase text-gray-500 dark:text-gray-400 sm:grid">
                <div className="col-span-4">Description</div>
                <div className="col-span-2">Quantity</div>
                <div className="col-span-2">Charges</div>
                <div className="col-span-2">Tax Rate</div>
                <div className="col-span-1 text-right">Amount</div>
                <div className="col-span-1" />
              </div>

              {/* Line item rows */}
              {fields.map((field, index) => {
                const qty = Number(watchedLineItems?.[index]?.quantity) || 0;
                const price = Number(watchedLineItems?.[index]?.unitPrice) || 0;
                const lineAmount = qty * price;

                return (
                  <div
                    key={field.id}
                    className="grid grid-cols-1 gap-2 rounded-lg border border-gray-200 p-3 dark:border-gray-700 sm:grid-cols-12 sm:border-0 sm:p-0"
                  >
                    <div className="sm:col-span-4">
                      <FormInput
                        name={`lineItems.${index}.description`}
                        control={control}
                        label={index === 0 ? undefined : undefined}
                        placeholder="Service description"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <FormInput
                        name={`lineItems.${index}.quantity`}
                        control={control}
                        type="number"
                        min={0.01}
                        step="0.01"
                        placeholder="1"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <FormInput
                        name={`lineItems.${index}.unitPrice`}
                        control={control}
                        type="number"
                        min={0}
                        step="0.01"
                        placeholder="0.00"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <FormSelect
                        name={`lineItems.${index}.taxRate`}
                        control={control}
                        options={taxRateOptions}
                      />
                    </div>
                    <div className="flex items-center justify-end sm:col-span-1">
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        ${lineAmount.toLocaleString('en-CA', { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                    <div className="flex items-center justify-end sm:col-span-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => remove(index)}
                        disabled={fields.length <= 1}
                        aria-label={`Remove line item ${index + 1}`}
                      >
                        <svg
                          className="h-4 w-4 text-red-500"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          aria-hidden="true"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                      </Button>
                    </div>
                  </div>
                );
              })}

              {/* Add line item button */}
              {fields.length < MAX_LINE_ITEMS && (
                <div className="flex items-center gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      append({
                        description: '',
                        quantity: 1,
                        unitPrice: 0,
                        taxRate: defaultTaxRateId || defaultTaxRateValue,
                      })
                    }
                  >
                    + Add Line Item
                  </Button>
                  {!isEditing && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setShowJobSearch(true)}
                    >
                      Import from Job
                    </Button>
                  )}
                  {(isAdmin || can(PERMISSIONS.BILLING_CREATE_DISCOUNT)) && (
                    <select
                      value={selectedDiscountId}
                      onChange={(e) => setSelectedDiscountId(e.target.value)}
                      className="h-8 rounded-lg border border-border bg-white px-2 text-xs dark:border-border-dark dark:bg-surface-dark-secondary dark:text-gray-100"
                      aria-label="Apply discount"
                    >
                      <option value="">No Discount</option>
                      {(discountDefinitions ?? [])
                        .filter((d) => d.isActive)
                        .map((d) => (
                          <option key={d.id} value={d.id}>
                            {d.label}
                          </option>
                        ))}
                    </select>
                  )}
                  {linkedJob && (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-50 px-2.5 py-0.5 text-xs font-medium text-brand-700 dark:bg-brand-900/20 dark:text-brand-300">
                      Linked: {linkedJob.jobCode || linkedJob.title}
                      <button
                        type="button"
                        onClick={() => setLinkedJob(null)}
                        className="ml-0.5 text-brand-500 hover:text-brand-700"
                        aria-label="Remove linked job"
                      >
                        &times;
                      </button>
                    </span>
                  )}
                </div>
              )}
              {fields.length >= MAX_LINE_ITEMS && (
                <p className="text-xs text-amber-600 dark:text-amber-400">
                  Maximum of {MAX_LINE_ITEMS} line items reached.
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Totals Preview */}
        <Card>
          <CardHeader title="Totals" />
          <CardContent>
            <div className="flex justify-end">
              <div className="w-72 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500 dark:text-gray-400">Subtotal</span>
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    ${totals.subtotal.toLocaleString('en-CA', { minimumFractionDigits: 2 })}
                  </span>
                </div>
                {totals.totalDiscount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-green-600 dark:text-green-400">
                      Discount{selectedDiscount ? ` (${selectedDiscount.label})` : ''}
                    </span>
                    <span className="font-medium text-green-600 dark:text-green-400">
                      -${totals.totalDiscount.toLocaleString('en-CA', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                )}
                {Object.entries(totals.taxByRate).map(([rate, { label, amount }]) => (
                  <div key={rate} className="flex justify-between text-sm">
                    <span className="text-gray-500 dark:text-gray-400">{label}</span>
                    <span className="text-gray-700 dark:text-gray-300">
                      ${amount.toLocaleString('en-CA', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                ))}
                <div className="flex justify-between border-t border-gray-200 pt-2 dark:border-gray-700">
                  <span className="font-semibold text-gray-900 dark:text-gray-100">
                    Grand Total
                  </span>
                  <span className="text-lg font-bold text-gray-900 dark:text-gray-100">
                    ${totals.grandTotal.toLocaleString('en-CA', { minimumFractionDigits: 2 })}
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Notes */}
        <Card>
          <CardHeader title="Notes" />
          <CardContent>
            <FormTextarea
              name="notes"
              control={control}
              placeholder="Additional notes or payment instructions..."
              rows={4}
            />
          </CardContent>
        </Card>
      </form>

      {/* Job Search Modal */}
      {showJobSearch && (
        <JobSearchModal
          onSelect={handleJobSelected}
          onClose={() => setShowJobSearch(false)}
          clientId={watch('clientId') || undefined}
        />
      )}
    </div>
  );
}
